/**
 * RequestsPanel.js — the "Requests" tab inside the notification center dialog
 * (see NotificationBell.js).
 *
 * Budget requests (the bottom-up half of the cascade — BudgetRequestControls.js)
 * used to be visible only inside the specific unit's Budget tab
 * (IncomingRequestsStrip / ChildRequestNote): a leader with requests waiting on
 * them across several units had no single place to see or act on them, and had
 * to remember which tier/unit page to open. This panel is that place — every
 * open request the signed-in user can decide on ("Waiting on you") or has
 * raised themselves ("Sent by you"), with Approve / Decline / Withdraw right
 * here.
 *
 * Only the right level acts (PO decision 2026-10-26): Approve / Decline are
 * offered only on a request the server says this viewer decides
 * (`canDecide` — the funding unit's own lead), Withdraw only where it says they
 * may take it back (`canWithdraw` — the asking unit's own lead). An admin sees
 * every request in the organization, so "Waiting on you" — and the count on
 * the bell — keep only what is actually waiting on THEM.
 *
 * Deliberately does not replicate the shortfall/escalate logic that
 * ChildRequestNote has: that needs the funding unit's live allocation table,
 * which this global panel does not load. Approving here always asks for the
 * requested amount as-is — the backend's own capacity check (the same one a
 * manual publish hits) either lands it as approved or refuses it outright
 * with the shortfall spelled out, so the two paths can never enforce
 * different rules. A refusal surfaces as an ordinary error here, not a
 * silent status change — there is no in-between state to render.
 */
import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import {
  Segmented, Empty, Spin, Button, Space, Tag, Typography, Input, message,
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { getTree, listTiers, decideBudgetRequest, withdrawBudgetRequest, listBudgetRequests } from '../services/orgStructureService';
import { useAuth } from '../contexts/AuthContext';
import { USD0 } from '../pages/discovery/sections/budgetFormat';

const { Text } = Typography;

const OPEN = new Set(['pending_approval', 'pending_capacity']);
const STATUS_TAG = {
  pending_approval: { color: 'blue', label: 'Awaiting approval' },
  pending_capacity: { color: 'orange', label: 'Waiting on capacity above' },
};

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

function timeAgo(iso) {
  if (!iso) {return '';}
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < SECONDS_PER_MINUTE) {return 'just now';}
  if (s < SECONDS_PER_HOUR) {return `${Math.floor(s / SECONDS_PER_MINUTE)}m ago`;}
  if (s < SECONDS_PER_DAY) {return `${Math.floor(s / SECONDS_PER_HOUR)}h ago`;}
  return `${Math.floor(s / SECONDS_PER_DAY)}d ago`;
}

/** Best-effort, readable label for a request's toUnitId/fromUnitId. Pure, so
 * the id/prefix/fallback rules are unit-testable without mounting a hook. */
export function resolveSubjectLabel(id, tierLevel, unitNameById = {}, tierNameByLevel = {}) {
  const s = String(id ?? '');
  if (s === '__org__') {return 'Organization';}
  if (s.startsWith('member:')) {return s.slice('member:'.length);}
  return unitNameById[s] || tierNameByLevel[tierLevel] || s;
}

/**
 * True when `request` was raised by `viewerEmail`, case-insensitively.
 * A blank viewer email (auth not resolved yet) never matches, even against a
 * malformed row with no requestedBy — "nothing" is the only safe answer while
 * we don't yet know who is asking, matching the `declareUsageScope('pending')`
 * convention used elsewhere in Discovery for the same reason.
 */
export function isOwnRequest(request, viewerEmail) {
  const self = (viewerEmail || '').toLowerCase();
  return !!self && (request?.requestedBy || '').toLowerCase() === self;
}

/**
 * The API's "outgoing" scope is deliberately broader than "sent by me" — it
 * also returns requests raised by someone ELSE for a unit you manage, so a
 * unit's own Budget tab can ask "does this unit have an open ask" regardless
 * of which of its co-leads raised it (see CascadeBudgetPanel / MyBudgetPanel,
 * which both filter that list down to one specific toUnitId before using it).
 * This dialog's tab has no single subject to filter to — it is labelled "Sent
 * by you", so it must actually mean that, or a manager ends up seeing a
 * teammate's request under their own name (the bug this narrowing fixes).
 */
export function filterOutgoingToSelf(list, viewerEmail) {
  return (list || []).filter((r) => isOwnRequest(r, viewerEmail));
}

/**
 * "Waiting on you" means waiting on YOUR decision. The API's incoming scope
 * hands an admin every open request in the organization (an admin may see
 * them all), so the list is narrowed to the ones the server says this viewer
 * decides.
 */
export function filterIncomingToDecider(list) {
  return (list || []).filter((r) => r.canDecide);
}

/** unitId -> unit name, walked from the org tree's roots. Pure/top-level so
 * the recursion isn't nested inside the fetch's own callback. */
function buildUnitNameMap(tree) {
  const byId = {};
  const walk = (nodes) => {
    (nodes || []).forEach((n) => {
      byId[String(n.id)] = n.name;
      walk(n.children);
    });
  };
  walk(tree?.roots || []);
  return byId;
}

/** tierLevel -> tierName, from the tiers-list response. */
function buildTierNameMap(tiersResponse) {
  const byLevel = {};
  (tiersResponse?.tiers || []).forEach((t) => { byLevel[t.tierLevel] = t.tierName; });
  return byLevel;
}

/** Best-effort, readable label for a request's toUnitId/fromUnitId. */
function useSubjectLabel() {
  const [unitNameById, setUnitNameById] = useState({});
  const [tierNameByLevel, setTierNameByLevel] = useState({});
  useEffect(() => {
    let on = true;
    Promise.all([getTree(), listTiers()]).then(([tree, tiers]) => {
      if (!on) {return;}
      setUnitNameById(buildUnitNameMap(tree));
      setTierNameByLevel(buildTierNameMap(tiers));
    }).catch(() => { /* raw ids are still readable enough as a fallback */ });
    return () => { on = false; };
  }, []);
  return useCallback(
    (id, tierLevel) => resolveSubjectLabel(id, tierLevel, unitNameById, tierNameByLevel),
    [unitNameById, tierNameByLevel],
  );
}

/** Decline-in-progress form: a reason textbox plus confirm/cancel. */
function DeclineForm({ note, onNoteChange, busy, onConfirm, onCancel }) {
  return (
    <div style={{ marginTop: 8 }}>
      <Input.TextArea
        rows={2} maxLength={200} value={note} autoFocus
        placeholder="Why — the requester sees this"
        onChange={onNoteChange}
      />
      <Space size={6} style={{ marginTop: 6 }}>
        <Button size="small" danger loading={busy} onClick={onConfirm}>Confirm decline</Button>
        <Button size="small" onClick={onCancel}>Cancel</Button>
      </Space>
    </div>
  );
}

/** Incoming, still-open request: Approve / Decline. */
function IncomingActions({ busy, requestedAmountUsd, onApprove, onStartDecline }) {
  return (
    <Space size={6} style={{ marginTop: 8 }}>
      <Button size="small" type="primary" ghost icon={<CheckOutlined />} loading={busy} onClick={onApprove}>
        Approve {USD0(requestedAmountUsd)}
      </Button>
      <Button size="small" icon={<CloseOutlined />} onClick={onStartDecline}>Decline</Button>
    </Space>
  );
}

/** Outgoing, still-open request: Withdraw. */
function WithdrawAction({ busy, onWithdraw }) {
  return (
    <Button
      size="small" type="link" style={{ padding: 0, height: 'auto', marginTop: 6 }}
      loading={busy} onClick={onWithdraw}
    >
      Withdraw
    </Button>
  );
}

/** What is asked, of whom, for when and by whom — the read-only part of a row. */
function RequestSummary({ request: r, label }) {
  const status = STATUS_TAG[r.status] || { color: 'default', label: r.status };
  // The payload's own names first: this panel is shown to people who cannot see
  // both of a request's units in their scoped org tree (the funder's lead is
  // handed an ask about a child unit), and naming them from that tree rendered
  // whatever was outside it as a raw `u-...` id. `label` stays as the fallback
  // for a response that carries no names.
  const subject = r.toUnitName || label(r.toUnitId, r.toTierLevel);
  const funder = r.fromUnitName || label(r.fromUnitId, r.fromTierLevel);
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <Text strong>{subject}</Text>
        <Tag color={status.color} style={{ marginInlineEnd: 0 }}>{status.label}</Tag>
      </div>
      <Text type="secondary" style={{ fontSize: 12.5 }}>
        {funder ? `Asking ${funder} for ` : 'For '}{r.period}
      </Text>
      <div style={{ marginTop: 4 }}>
        <Text>{USD0(r.currentAmountUsd)}</Text> <Text type="secondary">→</Text>{' '}
        <Text strong>{USD0(r.requestedAmountUsd)}</Text>{' '}
        <Text type="secondary">({r.deltaUsd >= 0 ? '+' : ''}{USD0(r.deltaUsd)})</Text>
      </div>
      {r.reason ? (
        <div style={{ marginTop: 2 }}>
          <Text type="secondary" style={{ fontSize: 12.5 }}>&ldquo;{r.reason}&rdquo;</Text>
        </div>
      ) : null}
      <div style={{ marginTop: 4 }}>
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          {r.requestedBy} · {timeAgo(r.createdAt)}
        </Text>
      </div>
    </>
  );
}

function RequestRow({ request: r, direction, label, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); message.success(okMsg); onChanged?.(); }
    catch (e) { message.error(e.message || 'Could not update the request'); }
    finally { setBusy(false); }
  };

  const approve = () => run(
    () => decideBudgetRequest(r.id, { decision: 'approve' }),
    `Approved at ${USD0(r.requestedAmountUsd)}`,
  );
  const decline = () => {
    if (!note.trim()) { message.warning('A reason is required to decline'); return; }
    run(
      () => decideBudgetRequest(r.id, { decision: 'decline', note: note.trim() }),
      'Declined',
    );
  };
  const withdraw = () => run(() => withdrawBudgetRequest(r.id), 'Withdrawn');

  const isIncomingActionable = direction === 'incoming' && OPEN.has(r.status) && !!r.canDecide;
  const isOutgoingActionable = direction === 'outgoing' && OPEN.has(r.status) && !!r.canWithdraw;

  return (
    <div style={{ padding: '12px 4px', borderBottom: '1px solid #f0f0f0' }}>
      <RequestSummary request={r} label={label} />
      {isIncomingActionable && (
        declining ? (
          <DeclineForm
            note={note}
            onNoteChange={(e) => setNote(e.target.value)}
            busy={busy}
            onConfirm={decline}
            onCancel={() => { setDeclining(false); setNote(''); }}
          />
        ) : (
          <IncomingActions
            busy={busy}
            requestedAmountUsd={r.requestedAmountUsd}
            onApprove={approve}
            onStartDecline={() => setDeclining(true)}
          />
        )
      )}
      {isOutgoingActionable && <WithdrawAction busy={busy} onWithdraw={withdraw} />}
    </div>
  );
}

/**
 * Forwards a `refresh()` handle so NotificationBell can re-fetch this panel's
 * current direction from the outside (on bell click, or the dialog's own
 * refresh button) — the panel otherwise only loads on mount/direction change,
 * and the Notification Center Modal keeps it mounted across opens
 * (destroyOnHidden is not set), so without this a reopen would show stale data.
 */
const RequestsPanel = React.forwardRef(function RequestsPanel({ onIncomingCountChange }, ref) {
  const { userEmail } = useAuth();
  const [direction, setDirection] = useState('incoming');
  const [rows, setRows] = useState(null); // null = loading
  const label = useSubjectLabel();

  const load = useCallback(async (dir) => {
    setRows(null);
    try {
      const out = await listBudgetRequests({ scope: dir });
      const list = dir === 'outgoing'
        ? filterOutgoingToSelf(out.requests || [], userEmail)
        : filterIncomingToDecider(out.requests || []);
      setRows(list);
      if (dir === 'incoming') {onIncomingCountChange?.(list.filter((r) => OPEN.has(r.status)).length);}
    } catch (_) {
      setRows([]);
    }
  }, [onIncomingCountChange, userEmail]);

  useEffect(() => { load(direction); }, [direction, load]);

  useImperativeHandle(ref, () => ({
    refresh: () => load(direction),
  }), [load, direction]);

  let body;
  if (rows === null) {
    body = <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
  } else if (rows.length === 0) {
    body = (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={direction === 'incoming' ? 'No requests waiting on you' : "You haven't asked for any budget changes"}
        style={{ margin: '30px 0' }}
      />
    );
  } else {
    body = (
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {rows.map((r) => (
          <RequestRow
            key={r.id} request={r} direction={direction} label={label}
            onChanged={() => load(direction)}
          />
        ))}
      </div>
    );
  }

  return (
    <div>
      <Segmented
        value={direction}
        onChange={setDirection}
        options={[
          { label: 'Waiting on you', value: 'incoming' },
          { label: 'Sent by you', value: 'outgoing' },
        ]}
        style={{ marginBottom: 10 }}
      />
      {body}
    </div>
  );
});

export default RequestsPanel;
